import { PlayerRef } from "@remotion/player";
import { MouseEventHandler, useCallback, useEffect, useState } from "react";

const calculateFrameFromMouseEvent = ({
  container,
  event,
  totalDurationInFrames,
}: {
  container: HTMLDivElement;
  event: React.MouseEvent<HTMLDivElement, MouseEvent> | MouseEvent;
  totalDurationInFrames: number;
}) => {
  const containerRect = container.getBoundingClientRect();
  if (!containerRect) {
    throw new Error("boundingRect is null");
  }

  const pixelsPerFrame = containerRect.width / totalDurationInFrames;

  const scrollX = container.scrollLeft;
  const clickPositionX = event.clientX - containerRect.x + scrollX;

  const frame = clickPositionX / pixelsPerFrame;

  const normalizedFrame = Math.max(
    0,
    Math.min(Math.round(frame), totalDurationInFrames - 1)
  );

  return normalizedFrame;
};

export const useTimelineSeek = ({
  containerRef,
  playerRef,
  totalDurationInFrames,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  playerRef: React.RefObject<PlayerRef | null>;
  totalDurationInFrames: number;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const frame = calculateFrameFromMouseEvent({
        container: containerRef.current,
        event: e,
        totalDurationInFrames,
      });

      playerRef.current?.seekTo(frame);
      e.preventDefault();
      e.stopPropagation();
    },
    [isDragging, containerRef, playerRef, totalDurationInFrames]
  );

  const stopDragging = useCallback(() => {
    setIsDragging(false);
  }, []);

  const startDragging: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (event.button !== 0) return;

      event.stopPropagation();

      setIsDragging(true);

      if (!containerRef.current) {
        throw new Error("Timeline container not found");
      }

      const frame = calculateFrameFromMouseEvent({
        container: containerRef.current,
        event,
        totalDurationInFrames,
      });

      playerRef.current?.seekTo(frame);
    },
    [containerRef, playerRef, totalDurationInFrames]
  );

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDragging);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
    };
  }, [handleMouseMove, stopDragging]);

  return {
    isDragging,
    startDragging,
    stopDragging,
  };
};
